// Session-log: fanger sessionens udgangspunkt ved landing og gemmer det i cookien
// "invictus_session_log" (persistent, 30 dage). Filen oprettes ALTID — indholdet er
// fastlagt her i koden (ingen master-kontakt eller felt-flag). Cookien er kun en
// first-touch sessionslog til attribution og ordre-snapshot; den bruges ALDRIG som
// kilde til CountryId, priser, valuta eller sprog. UTM styrer dog den godkendte
// kampagnelanding og rabatbadge-visning.
//
// Cookien beholder attribution, mens VisitTimestamp, DeviceType og ReferralCode
// kun findes i den aktuelle SessionContext (Step 3–5).
// Felter (4) — brugerens landing-flow:
//   Rå (fanges ved FØRSTE landing og står fast — first-touch):
//     Country        (Step 1: fra IP via serveren — aldrig gættet)
//     Language       (Step 2: enhedens sprog hvis accepteret, ellers landets —
//                     kun hvis filen ikke allerede har et sprog)
//   Afledte (genberegnes ved HVER landing):
//     CampaignActive (Step 6: YES/NO fra kampagnedato+time i landets tidszone —
//                     serveren returnerer NO ved gyldig henvisningskode)
//     UTM_Campaigncode (Step 7: en konkret, whitelisted kampagnekode eller NONE)
// Forbrug: ved gennemført køb ryddes UTM_Campaigncode (→NONE). ReferralCode forbliver kun live
// i SessionContext og gemmes på den gennemførte ordre som referral_code.

const COOKIE = "invictus_session_log";
const MAX_AGE = 30 * 24 * 60 * 60; // 30 dage

const queryParamCI = (name) => {
  const wanted = String(name).toLowerCase();
  for (const [key, value] of new URLSearchParams(window.location.search).entries()) {
    if (key.toLowerCase() === wanted) return value;
  }
  return "";
};

const getCookie = (name) => {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
};

const writeCookie = (data) => {
  document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(data))}; path=/; SameSite=Lax; max-age=${MAX_AGE}`;
};

export const detectDeviceType = () => {
  const ua = navigator.userAgent;
  if (/iPad|Tablet/i.test(ua)) return "Tablet";
  if (/Mobi|Android|iPhone/i.test(ua)) return "Mobil";
  return "Desktop";
};

// Rå felter har hver sin fanger. ctx kommer fra landing-evalueringen:
// { countryId, language }
const RAW_CAPTURERS = {
  Country: (ctx) => ctx.countryId || "",
  Language: (ctx) => ctx.language || navigator.language || "",
};

const stripContextOnlyFields = (data) => {
  const next = { ...(data || {}) };
  delete next.VisitTimestamp;
  delete next.DeviceType;
  delete next.ReferralCode;
  delete next.UTM; // gammel YES/NO-kontrakt må aldrig genbruges
  if (!String(next.UTM_Campaigncode || "").trim()) next.UTM_Campaigncode = "NONE";
  return next;
};

const whitelistedCampaignCode = (value, whitelist) => {
  const wanted = String(value || "").trim().toLowerCase();
  if (!wanted || wanted === "none") return "NONE";
  const match = (Array.isArray(whitelist) ? whitelist : []).find(
    (code) => String(code || "").trim().toLowerCase() === wanted
  );
  return match ? String(match).trim() : "NONE";
};

// Step 07: gemt gyldig kode → gyldig URL-kode → NONE. CampaignActive er ikke en
// kodevalideringsregel; den er fortsat alene rabat-/kampagne-gaten i Step 06.
// Kaldes ved hver landing (fra SessionContext). ctx: { countryId, campaignActive, language }
export function captureSessionLog(config, ctx = {}) {
  try {
    const campaignActive = ctx.campaignActive ? "YES" : "NO";
    const existing = readSessionLog();
    const storedCode = whitelistedCampaignCode(existing?.UTM_Campaigncode, config?.utm_whitelist);
    const urlCode = whitelistedCampaignCode(queryParamCI("utm_campaign"), config?.utm_whitelist);
    const utmCampaignCode = storedCode !== "NONE" ? storedCode : urlCode;

    if (!existing) {
      // Første landing: rå felter fanges og står fast (first-touch attribution)
      const data = {};
      for (const [name, capture] of Object.entries(RAW_CAPTURERS)) {
        data[name] = capture(ctx) ?? "";
      }
      data.CampaignActive = campaignActive;
      data.UTM_Campaigncode = utmCampaignCode;
      writeCookie(data);
      return data;
    }

    // Senere besøg: de rå felter står fast.
    const data = stripContextOnlyFields(existing);
    // Step 2: Et gyldigt gemt sprog bevares. En tom eller ugyldig værdi
    // erstattes af det netop evaluerede sprog fra enhed/land.
    const currentLanguage = String(data.Language || "").toLowerCase().split("-", 1)[0];
    if (!currentLanguage || currentLanguage !== String(ctx.language || "").toLowerCase()) {
      data.Language = RAW_CAPTURERS.Language(ctx);
    }
    // Afledte felter genberegnes ved hver landing (kampagnen kan være startet/stoppet)
    data.CampaignActive = campaignActive;
    data.UTM_Campaigncode = utmCampaignCode;
    writeCookie(data);
    return data;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") console.warn("Sessionlog kunne ikke opdateres:", error);
  }
}

// Læses ind i sessionContext ved landing og sendes med ordren som snapshot ved checkout
export function readSessionLog() {
  try {
    const raw = getCookie(COOKIE);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const cleaned = stripContextOnlyFields(data);
    if (JSON.stringify(cleaned) !== JSON.stringify(data)) writeCookie(cleaned);
    return cleaned;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") console.warn("Sessionlog kunne ikke læses:", error);
    return null;
  }
}

// Opdatér enkelte persistente attribution-felter i filen (fx Language ved flagvalg).
export function updateSessionLog(patch) {
  try {
    const data = readSessionLog();
    if (!data) return null;
    const next = stripContextOnlyFields({ ...data, ...patch });
    writeCookie(next);
    return next;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") console.warn("Sessionlog kunne ikke gemmes:", error);
    return null;
  }
}

// Ved bekræftet ordre ryddes værdierne, så de ikke kan genbruges.
export function consumeSessionCodes() {
  const data = readSessionLog();
  if (!data) return null;
  const next = stripContextOnlyFields(data);
  next.UTM_Campaigncode = "NONE";
  writeCookie(next);
  return next;
}
